import { useState } from 'react';
import {
  Alert,
  App as AntApp,
  Avatar,
  Button,
  Card,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PoweroffOutlined, UserAddOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@starling/core/api/errors';
import { ErrorAlert } from '@/components/ErrorAlert';
import { useAuthStore } from '@starling/core/auth/store';
import {
  usePlatformAdminsQuery,
  useUpdatePlatformAdminMutation,
  type PlatformAdminRead,
} from './hooks';
import { AddPlatformAdminModal } from './AddPlatformAdminModal';

const { Title, Text } = Typography;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

// Управление суперадминами платформы (M5.4). Список + добавить + включить/отключить.
// 409 cannot_disable_last_admin → предупреждение. Образец CRUD — @/users/.
export function PlatformAdminsPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data, isLoading, error } = usePlatformAdminsQuery();
  const updateMutation = useUpdatePlatformAdminMutation();
  const [addOpen, setAddOpen] = useState(false);

  const admins = data?.items ?? [];

  const handleToggle = (admin: PlatformAdminRead): void => {
    const next = admin.status === 'active' ? 'disabled' : 'active';
    updateMutation.mutate(
      { id: admin.id, status: next },
      {
        onSuccess: () => {
          void message.success(
            next === 'active' ? t('platform.admins.enabled') : t('platform.admins.disabled'),
          );
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'cannot_disable_last_admin') {
            void message.error(t('platform.admins.errors.cannot_disable_last'));
          } else {
            void message.error(err instanceof Error ? err.message : t('errors.generic'));
          }
        },
      },
    );
  };

  const columns: ColumnsType<PlatformAdminRead> = [
    {
      title: t('platform.admins.table.name'),
      key: 'name',
      render: (_v, a) => {
        const isYou = a.id === currentUserId;
        const active = a.status === 'active';
        return (
          <Space size={10}>
            <Avatar size="small" style={{ opacity: active ? 1 : 0.5 }}>
              {initials(a.name) || '?'}
            </Avatar>
            <Text strong={active} type={active ? undefined : 'secondary'}>
              {a.name}
            </Text>
            {isYou && <Tag>{t('platform.admins.you')}</Tag>}
          </Space>
        );
      },
    },
    {
      title: t('platform.admins.table.email'),
      dataIndex: 'email',
      key: 'email',
      render: (v: string) => (
        <span className="sl-mono" style={{ fontSize: 12 }}>
          {v}
        </span>
      ),
    },
    {
      title: t('platform.admins.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (st: PlatformAdminRead['status']) =>
        st === 'active' ? (
          <Tag color="green">{t('platform.admins.status.active')}</Tag>
        ) : (
          <Tag>{t('platform.admins.status.disabled')}</Tag>
        ),
    },
    {
      title: t('platform.admins.table.action'),
      key: 'action',
      width: 150,
      align: 'right',
      render: (_v, a) => {
        if (a.id === currentUserId) {
          return <Text type="secondary">—</Text>;
        }
        const active = a.status === 'active';
        const btn = (
          <Button
            size="small"
            icon={<PoweroffOutlined />}
            loading={updateMutation.isPending && updateMutation.variables?.id === a.id}
            onClick={active ? undefined : () => handleToggle(a)}
          >
            {active ? t('platform.admins.disable') : t('platform.admins.enable')}
          </Button>
        );
        // Отключение — с подтверждением; включение — сразу.
        return active ? (
          <Popconfirm
            title={t('platform.admins.disable_confirm')}
            okText={t('platform.admins.disable')}
            cancelText={t('common.cancel')}
            onConfirm={() => handleToggle(a)}
          >
            {btn}
          </Popconfirm>
        ) : (
          btn
        );
      },
    },
  ];

  return (
    <div>
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('platform.admins.title')}
          </Title>
          <Text type="secondary">{t('platform.admins.subtitle')}</Text>
        </div>
        <Button type="primary" icon={<UserAddOutlined />} onClick={() => setAddOpen(true)}>
          {t('platform.admins.add.title')}
        </Button>
      </Space>

      {error && (
        <div style={{ marginTop: 16 }}>
          <ErrorAlert error={error} />
        </div>
      )}

      <Card style={{ marginTop: 16 }} variant="outlined" styles={{ body: { padding: 0 } }}>
        <Table<PlatformAdminRead>
          rowKey="id"
          loading={isLoading}
          dataSource={admins}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('platform.admins.empty') }}
        />
      </Card>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16 }}
        message={t('platform.admins.last_admin_hint')}
      />

      <AddPlatformAdminModal open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}
